import { Call, Device } from '@twilio/voice-sdk';
import { setIncomingCall, setCallType, setEndCall, apiAcceptInboundCall } from 'state/call.state';
import store from 'state/store';

import { Lead, Concierge, CallTypes, CallToken } from 'typings';

export class TwilioClient {
  private device?: Device;
  private call?: Call;

  initCallDevice(callToken: CallToken) {
    if (this.device) {
      console.log('Twilio: Destroying device');
      this.device.destroy();
    }

    const options: Device.Options = {
      logLevel: 'DEBUG',
      allowIncomingWhileBusy: false,
      enableImprovedSignalingErrorPrecision: true,
    };

    try {
      this.device = new Device(callToken.token, options);
      console.log('Twilio: Initialized device');

      this.registerDeviceEvents();

      // Register for inbound calls
      this.device.register();
      console.log('Twilio: Registered device');
    } catch (error) {
      console.error('Twilio: Error initializing device', { error });
    }
  }

  private registerDeviceEvents() {
    if (!this.device) return;

    this.device.on('destroyed', () => {
      console.error('Twilio device event: destroyed');

      this.device = undefined;
    });

    this.device.on('error', (error, call) =>
      console.error('Twilio device event: error', { error, call })
    );

    this.device.on('incoming', (call: Call) => {
      console.log('Twilio device event: incoming', { call });

      this.call = call;
      this.registerCallEvents();

      this.call.accept();
      console.log('Twilio: Accepted incoming call');
    });

    console.log('Twilio: Registered device events');
  }

  private registerCallEvents() {
    if (!this.call) return;

    this.call.on('disconnect', () => {
      console.log('Twilio call event: disconnect');

      this.call = undefined;
      store.dispatch(setEndCall());
    });

    this.call.on('error', (error) => console.error('Twilio call event: error', { error }));

    if (this.call.direction === Call.CallDirection.Incoming) {
      this.call.on('accept', () => {
        console.log('Twilio call event: accept');

        store.dispatch(setIncomingCall(false));
        store.dispatch(setCallType(CallTypes.INBOUND));
      });
    }

    console.log('Twilio: Registered call events');
  }

  async dialOutbound(lead: Lead, concierge: Concierge) {
    if (!this.device || !lead.phone || (!lead.engagedPhone && !lead.agent?.twilioNumber)) return;

    const params: Record<string, string> = {
      To: lead.phone,
      From: lead.engagedPhone || lead.agent?.twilioNumber || '',
      Direction: 'outbound',
      LeadId: lead.id,
      ConciergeId: String(concierge.id),
    };

    this.call = await this.device.connect({ params });
    console.log('Twilio: Dialed outbound', { params, call: this.call });

    this.registerCallEvents();
    store.dispatch(setCallType(CallTypes.OUTBOUND));
  }

  acceptInbound() {
    store.dispatch(apiAcceptInboundCall());
  }

  muteCall(shouldMute: boolean) {
    if (!this.call) return;

    this.call.mute(shouldMute);
    console.log('Twilio: Muted call', { shouldMute });
  }

  endCall() {
    if (!this.device) return;

    this.device.disconnectAll();
    console.log('Twilio: Disconnected all calls');
  }

  /**
   * @param digits - can contain the characters 0-9, *, #, and w.
   */
  sendDigits(digits: string) {
    if (!this.call) return;

    this.call.sendDigits(digits);
    console.log('Twilio: Sent digits', { digits });
  }

  needsRing() {
    return false;
  }
}
